import { useEffect, useState, type ReactNode } from 'react';
import { IntlProvider } from 'use-intl';
import type { AbstractIntlMessages } from 'use-intl';

import { Spinner } from '@shared';

import { detectLocale } from './detect-locale';
import { loadMessages } from './load-messages';
import { LocaleSetterContext, type SetLocale } from './locale-context';
import { LOCALE_STORAGE_KEY, type Locale } from './locales';

export type { SetLocale };

/**
 * Loads the message catalogue for the active locale and provides it to the tree.
 * There is no visible language switcher yet — the app ships German only — but the
 * setter is already wired so adding one is a UI-only change.
 */
export function IntlProviderShell({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());
  const [messages, setMessages] = useState<AbstractIntlMessages | null>(null);

  useEffect(() => {
    void loadMessages(locale).then(setMessages);
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  function setLocale(next: Locale): void {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // Storage unavailable (private mode / partitioned iframe) — the choice
      // simply does not survive a reload.
    }
    setLocaleState(next);
  }

  if (!messages) {
    return (
      <div className="zs-center">
        <Spinner />
      </div>
    );
  }

  return (
    <LocaleSetterContext.Provider value={setLocale}>
      <IntlProvider
        locale={locale}
        messages={messages}
        onError={(error) => {
          const code = (error as { code?: string }).code;

          // A missing key should degrade gracefully (fallback text) and never blank the app.
          if (code === 'MISSING_MESSAGE') {
            if (import.meta.env.DEV) {
              console.warn('[MDenture] Missing i18n message', error);
            }
            return;
          }

          throw error;
        }}
      >
        {children}
      </IntlProvider>
    </LocaleSetterContext.Provider>
  );
}
